/**
 * ApprovalPrompt — pops up for managers anywhere in the app when a cashier
 * asks them to approve a return. Checks every 15 s while the tab is open
 * (paused in background tabs). "Later" hides that request here; it stays
 * in the notification bell and the email link still works.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { playEazeSound } from "@/lib/eaze-sound";
import { showSystemNotification } from "@/lib/system-notify";
import { useQuery } from "@tanstack/react-query";
import { BellRing } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import useAuthStore from "@/stores/authStore";
import approvalService from "@/services/approvalService";
import ApprovalCard from "./ApprovalCard";

const APPROVER_ROLES = new Set(["tenant_owner", "super_admin", "business_admin"]);

export default function ApprovalPrompt() {
  const role = useAuthStore((s) => s.user?.role);
  const enabled = !!role && APPROVER_ROLES.has(role);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const { data } = useQuery({
    queryKey: ["approvals", "pending"],
    queryFn: () => approvalService.pending(),
    enabled,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
    staleTime: 10_000,
  });

  // A new request waiting on you: the Eaze "attention" chime (and the device's
  // own notification if the tab is in the background).
  const known = useRef<Set<string> | null>(null);
  useEffect(() => {
    if (!data) return;
    if (!known.current) {
      known.current = new Set(data.map((r) => r._id));
      return;
    }
    const fresh = data.filter((r) => !known.current!.has(r._id));
    fresh.forEach((r) => known.current!.add(r._id));
    if (!fresh.length) return;
    playEazeSound("attention");
    if (document.visibilityState === "hidden") {
      const r = fresh[0];
      void showSystemNotification({ title: r.kind === "credit_sale" ? "A credit sale needs your approval" : "A return needs your approval", body: r.summary?.businessName || "The cashier is waiting with the customer.", tag: `eaze-approval-${r._id}`, url: `/approvals/${r._id}` });
    }
  }, [data]);

  const next = useMemo(() => (data ?? []).find((r) => !dismissed.has(r._id)), [data, dismissed]);
  const waiting = (data ?? []).filter((r) => !dismissed.has(r._id)).length;
  if (!enabled || !next) return null;

  const later = () => setDismissed((d) => new Set(d).add(next._id));

  return (
    <Dialog open onOpenChange={(o) => !o && later()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-amber-600" aria-hidden />
            {next.kind === "credit_sale" ? "A credit sale needs your approval" : "A return needs your approval"}
          </DialogTitle>
          <DialogDescription>
            {next.summary.businessName ? `${next.summary.businessName} · ` : ""}
            {waiting > 1 ? `${waiting} requests waiting` : "The cashier is waiting with the customer."}
          </DialogDescription>
        </DialogHeader>
        <ApprovalCard key={next._id} request={next} onDone={later} />
        <div className="flex justify-end">
          <Button variant="ghost" size="sm" onClick={later}>
            Later
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
